"use client";

import { useState, useEffect } from "react";
import { ArrowLeft, ArrowRight, MessageSquare, Mic, Sparkles, Bird, ChevronRight, Send } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import type { UserProfile } from "@/lib/settings-context";
import type { KnownLanguageCode } from "@/lib/api/client";
import { useTranslations } from "@/lib/use-translations";
import { useLanguage, languages, targetLanguages } from "@/lib/language-context";
import { useTTS } from "@/lib/use-tts";
import { SpeakerButton } from "@/components/speaker-button";

interface OnboardingWizardProps {
  onComplete: (profile: UserProfile, analyticsEnabled: boolean) => void;
}

type Step =
  | "welcome"
  | "feature-topics"
  | "feature-voice"
  | "feature-ai"
  | "input-native-lang"
  | "input-target-lang"
  | "input-name"
  | "input-languages"
  | "input-style"
  | "input-gender"
  | "ready"
  | "first-conversation";

const FEATURE_STEPS: Step[] = ["welcome", "feature-topics", "feature-voice", "feature-ai"];
const INPUT_STEPS: Step[] = ["input-native-lang", "input-target-lang", "input-name", "input-gender"];
const ALL_STEPS: Step[] = [...FEATURE_STEPS, ...INPUT_STEPS, "ready", "first-conversation"];

interface LanguageOption {
  code: KnownLanguageCode;  // ISO 639-1 code (e.g., "es", "he")
  name: string;             // Display name
  flag: string;
}

const COMMON_LANGUAGES: LanguageOption[] = [
  { code: "es", name: "Spanish", flag: "🇪🇸" },
  { code: "fr", name: "French", flag: "🇫🇷" },
  { code: "de", name: "German", flag: "🇩🇪" },
  { code: "it", name: "Italian", flag: "🇮🇹" },
  { code: "pt", name: "Portuguese", flag: "🇵🇹" },
  { code: "ru", name: "Russian", flag: "🇷🇺" },
  { code: "zh", name: "Chinese", flag: "🇨🇳" },
  { code: "ja", name: "Japanese", flag: "🇯🇵" },
  { code: "ko", name: "Korean", flag: "🇰🇷" },
  { code: "ar", name: "Arabic", flag: "🇸🇦" },
  { code: "hi", name: "Hindi", flag: "🇮🇳" },
  { code: "nl", name: "Dutch", flag: "🇳🇱" },
  { code: "pl", name: "Polish", flag: "🇵🇱" },
  { code: "sv", name: "Swedish", flag: "🇸🇪" },
];

const ALL_LANGUAGES: LanguageOption[] = [
  ...COMMON_LANGUAGES,
  { code: "tr", name: "Turkish", flag: "🇹🇷" },
  { code: "el", name: "Greek", flag: "🇬🇷" },
  { code: "cs", name: "Czech", flag: "🇨🇿" },
  { code: "ro", name: "Romanian", flag: "🇷🇴" },
  { code: "hu", name: "Hungarian", flag: "🇭🇺" },
  { code: "fi", name: "Finnish", flag: "🇫🇮" },
  { code: "no", name: "Norwegian", flag: "🇳🇴" },
  { code: "da", name: "Danish", flag: "🇩🇰" },
  { code: "uk", name: "Ukrainian", flag: "🇺🇦" },
  { code: "th", name: "Thai", flag: "🇹🇭" },
  { code: "vi", name: "Vietnamese", flag: "🇻🇳" },
  { code: "id", name: "Indonesian", flag: "🇮🇩" },
  { code: "ms", name: "Malay", flag: "🇲🇾" },
  { code: "tl", name: "Tagalog", flag: "🇵🇭" },
  { code: "he", name: "Hebrew", flag: "🇮🇱" },
  { code: "fa", name: "Persian", flag: "🇮🇷" },
  { code: "bn", name: "Bengali", flag: "🇧🇩" },
  { code: "pa", name: "Punjabi", flag: "🇮🇳" },
  { code: "ta", name: "Tamil", flag: "🇮🇳" },
  { code: "ur", name: "Urdu", flag: "🇵🇰" },
  { code: "sw", name: "Swahili", flag: "🇰🇪" },
  { code: "af", name: "Afrikaans", flag: "🇿🇦" },
  { code: "ca", name: "Catalan", flag: "🇪🇸" },
  { code: "hr", name: "Croatian", flag: "🇭🇷" },
  { code: "sr", name: "Serbian", flag: "🇷🇸" },
  { code: "sk", name: "Slovak", flag: "🇸🇰" },
  { code: "sl", name: "Slovenian", flag: "🇸🇮" },
  { code: "bg", name: "Bulgarian", flag: "🇧🇬" },
  { code: "lt", name: "Lithuanian", flag: "🇱🇹" },
  { code: "lv", name: "Latvian", flag: "🇱🇻" },
  { code: "et", name: "Estonian", flag: "🇪🇪" },
  { code: "is", name: "Icelandic", flag: "🇮🇸" },
  { code: "cy", name: "Welsh", flag: "🏴󠁧󠁢󠁷󠁬󠁳󠁿" },
  { code: "ga", name: "Irish", flag: "🇮🇪" },
  { code: "gd", name: "Scottish Gaelic", flag: "🏴󠁧󠁢󠁳󠁣󠁴󠁿" },
  { code: "eu", name: "Basque", flag: "🇪🇸" },
  { code: "mt", name: "Maltese", flag: "🇲🇹" },
  { code: "sq", name: "Albanian", flag: "🇦🇱" },
  { code: "mk", name: "Macedonian", flag: "🇲🇰" },
  { code: "ka", name: "Georgian", flag: "🇬🇪" },
  { code: "hy", name: "Armenian", flag: "🇦🇲" },
  { code: "kk", name: "Kazakh", flag: "🇰🇿" },
  { code: "uz", name: "Uzbek", flag: "🇺🇿" },
  { code: "mn", name: "Mongolian", flag: "🇲🇳" },
  { code: "ne", name: "Nepali", flag: "🇳🇵" },
  { code: "si", name: "Sinhala", flag: "🇱🇰" },
  { code: "my", name: "Burmese", flag: "🇲🇲" },
  { code: "km", name: "Khmer", flag: "🇰🇭" },
  { code: "lo", name: "Lao", flag: "🇱🇦" },
];

export function OnboardingWizard({ onComplete }: OnboardingWizardProps) {
  const { isLoaded, ...t } = useTranslations();
  const { nativeLanguage, targetLanguage, setNativeLanguage, setTargetLanguage } = useLanguage();
  const [currentStep, setCurrentStep] = useState<Step>("welcome");
  const [profile, setProfile] = useState<UserProfile>({
    name: "",
    gender: "",
    speakingStyle: "",
    customStyles: [],
    knownLanguages: [],
  });
  // For "Mix it up" style multi-selection (synced with profile.customStyles)
  const [isOtherExpanded, setIsOtherExpanded] = useState(false);
  const [_customStyleInput, _setCustomStyleInput] = useState("");
  const [_showCustomInput, _setShowCustomInput] = useState(false);
  // For known languages (synced with profile.knownLanguages)
  const [customLanguageInput, setCustomLanguageInput] = useState("");
  const [showCustomLanguage, setShowCustomLanguage] = useState(false);
  // Analytics opt-in (default to true)
  const [analyticsEnabled, setAnalyticsEnabled] = useState(true);

  // Auto-detect browser language and pre-select native language
  useEffect(() => {
    try {
      const browserLangs = navigator.languages ?? [navigator.language];
      for (const browserLang of browserLangs) {
        const match = languages.find(
          (l) =>
            l.code === browserLang ||
            l.code.split("-")[0] === browserLang.split("-")[0]
        );
        if (match) {
          setNativeLanguage(match);
          break;
        }
      }
    } catch {
      // Ignore — keep default
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const currentStepIndex = ALL_STEPS.indexOf(currentStep);
  const isInputStep = INPUT_STEPS.includes(currentStep);

  // Wait for locale to be loaded before rendering
  if (!isLoaded) {
    return null;
  }

  const goToNextStep = () => {
    const nextIndex = currentStepIndex + 1;
    const nextStep = ALL_STEPS[nextIndex];
    if (nextStep) {
      setCurrentStep(nextStep);
    }
  };

  const goToPrevStep = () => {
    const prevIndex = currentStepIndex - 1;
    const prevStep = ALL_STEPS[prevIndex];
    if (prevStep) {
      setCurrentStep(prevStep);
    }
  };

  const skipToEnd = () => {
    setCurrentStep("first-conversation");
  };

  const handleComplete = () => {
    onComplete(profile, analyticsEnabled);
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case "welcome":
        return (
          <div className="flex flex-col items-center justify-center min-h-[60vh] px-6 text-center">
            <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
              <Bird className="w-10 h-10 text-primary" />
            </div>
            <h1 className="text-3xl font-display text-foreground mb-3">
              {t.onboarding.welcomeTitle}
            </h1>
            <p className="text-lg text-muted-foreground max-w-sm mb-8">
              {t.onboarding.welcomeSubtitle}
            </p>
            <Button
              onClick={goToNextStep}
              size="lg"
              className="gap-2"
            >
              {t.onboarding.getStarted}
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        );

      case "feature-topics":
        return (
          <FeatureSlide
            icon={<MessageSquare className="w-10 h-10 text-primary" />}
            painPoint={t.onboarding.featureTopicsPain}
            solution={t.onboarding.featureTopicsSolution}
            description={t.onboarding.featureTopicsDesc}
            preview={
              <TopicCreatorPreview
                title={t.onboarding.previewTopicTitle}
                subtitle={t.onboarding.previewTopicSubtitle}
                exampleLabel={t.onboarding.previewExample}
                exampleText={t.onboarding.previewExampleText}
                typeOwnPlaceholder={t.onboarding.previewTypeOwn}
              />
            }
            onNext={goToNextStep}
            continueLabel={t.onboarding.continue}
          />
        );

      case "feature-voice":
        return (
          <FeatureSlide
            icon={<Mic className="w-10 h-10 text-primary" />}
            painPoint={t.onboarding.featureVoicePain}
            solution={t.onboarding.featureVoiceSolution}
            description={t.onboarding.featureVoiceDesc}
            preview={<VoiceInputPreview voiceText={t.onboarding.previewVoiceText} />}
            onNext={goToNextStep}
            continueLabel={t.onboarding.continue}
          />
        );

      case "feature-ai":
        return (
          <FeatureSlide
            icon={<Sparkles className="w-10 h-10 text-primary" />}
            painPoint={t.onboarding.featureAiPain}
            solution={t.onboarding.featureAiSolution}
            description={t.onboarding.featureAiDesc}
            preview={<DialoguePreview translation={t.onboarding.previewDialogTranslation} />}
            onNext={goToNextStep}
            continueLabel={t.onboarding.continue}
          />
        );

      case "input-native-lang":
        return (
          <InputStep
            title={t.onboarding.inputNativeLangTitle}
            subtitle={t.onboarding.inputNativeLangSubtitle}
            onNext={goToNextStep}
            onSkip={goToNextStep}
            hideDefaultButtons
            continueLabel={t.onboarding.continue}
            skipLabel={t.onboarding.skipStep}
          >
            <div className="grid grid-cols-2 gap-2 w-full">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  type="button"
                  onClick={() => {
                    setNativeLanguage(lang);
                    goToNextStep();
                  }}
                  className={cn(
                    "p-3 text-left rounded-xl border-2 transition-all flex items-center gap-2",
                    nativeLanguage.code === lang.code
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-primary/50 hover:bg-muted/50"
                  )}
                >
                  <span className="text-xl">{lang.flag}</span>
                  <div className="min-w-0">
                    <div className="font-medium text-sm text-foreground truncate">{lang.nativeName}</div>
                    {lang.region && (
                      <div className="text-xs text-muted-foreground">{lang.region}</div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </InputStep>
        );

      case "input-target-lang": {
        // Filter out native language from target options
        const availableTargets = targetLanguages.filter(
          (lang) => lang.code.split("-")[0] !== nativeLanguage.code.split("-")[0]
        );

        return (
          <InputStep
            title={t.onboarding.inputTargetLangTitle}
            subtitle={t.onboarding.inputTargetLangSubtitle}
            onNext={goToNextStep}
            onSkip={goToNextStep}
            hideDefaultButtons
            continueLabel={t.onboarding.continue}
            skipLabel={t.onboarding.skipStep}
          >
            <div className="grid grid-cols-2 gap-2 w-full">
              {availableTargets.map((lang) => (
                <button
                  key={lang.code}
                  type="button"
                  onClick={() => {
                    setTargetLanguage(lang);
                    goToNextStep();
                  }}
                  className={cn(
                    "p-3 text-left rounded-xl border-2 transition-all flex items-center gap-2",
                    targetLanguage.code === lang.code
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-primary/50 hover:bg-muted/50"
                  )}
                >
                  <span className="text-xl">{lang.flag}</span>
                  <div className="min-w-0">
                    <div className="font-medium text-sm text-foreground truncate">{lang.nativeName}</div>
                    {lang.region && (
                      <div className="text-xs text-muted-foreground">{lang.region}</div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </InputStep>
        );
      }

      case "input-name":
        return (
          <InputStep
            title={t.onboarding.inputNameTitle}
            subtitle={t.onboarding.inputNameSubtitle}
            onNext={goToNextStep}
            onSkip={goToNextStep}
            continueLabel={t.onboarding.continue}
            skipLabel={t.onboarding.skipStep}
          >
            <input
              type="text"
              value={profile.name}
              onChange={(e) => setProfile({ ...profile, name: e.target.value })}
              placeholder={t.onboarding.inputNamePlaceholder}
              className="w-full px-4 py-3 text-lg bg-background border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/50 text-center"
              autoFocus
            />
          </InputStep>
        );

      case "input-languages": {
        // Filter languages for search dropdown (exclude already selected and common ones shown as pills)
        const commonLanguageCodes = COMMON_LANGUAGES.map(l => l.code);
        const searchResults = customLanguageInput.trim()
          ? ALL_LANGUAGES.filter(
              l =>
                l.name.toLowerCase().includes(customLanguageInput.toLowerCase()) &&
                !profile.knownLanguages.includes(l.code) &&
                !commonLanguageCodes.includes(l.code)
            ).slice(0, 6)
          : [];

        const addLanguage = (langCode: KnownLanguageCode) => {
          setProfile(prev => ({ ...prev, knownLanguages: [...prev.knownLanguages, langCode] }));
        };
        const removeLanguage = (langCode: KnownLanguageCode) => {
          setProfile(prev => ({ ...prev, knownLanguages: prev.knownLanguages.filter(l => l !== langCode) }));
        };

        return (
          <InputStep
            title={t.onboarding.inputLangTitle}
            subtitle={t.onboarding.inputLangSubtitle}
            onNext={goToNextStep}
            onSkip={goToNextStep}
            hideDefaultButtons={profile.knownLanguages.length > 0 || showCustomLanguage}
            continueLabel={t.onboarding.continue}
            skipLabel={t.onboarding.skipStep}
          >
            <div className="space-y-4 w-full">
              <p className="text-sm text-muted-foreground text-center">
                {t.onboarding.inputLangSelect}
              </p>
              <div className="flex flex-wrap gap-2 justify-center">
                {COMMON_LANGUAGES.map((language) => {
                  const isSelected = profile.knownLanguages.includes(language.code);
                  return (
                    <button
                      key={language.code}
                      type="button"
                      onClick={() => {
                        if (isSelected) {
                          removeLanguage(language.code);
                        } else {
                          addLanguage(language.code);
                        }
                      }}
                      className={cn(
                        "px-3 py-1.5 text-sm rounded-full border transition-colors flex items-center gap-1.5",
                        isSelected
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-muted/50 text-muted-foreground border-border hover:border-primary/50"
                      )}
                    >
                      <span className={cn(
                        "w-3.5 h-3.5 rounded border flex items-center justify-center text-[10px]",
                        isSelected
                          ? "bg-primary-foreground/20 border-primary-foreground/50"
                          : "border-current"
                      )}>
                        {isSelected && "✓"}
                      </span>
                      <span>{language.flag}</span>
                      {language.name}
                    </button>
                  );
                })}
                {/* Search for more - outline pill */}
                <button
                  type="button"
                  onClick={() => setShowCustomLanguage(!showCustomLanguage)}
                  className={cn(
                    "px-3 py-1.5 text-sm rounded-full border-2 border-dashed transition-colors",
                    showCustomLanguage
                      ? "border-primary text-primary"
                      : "border-muted-foreground/50 text-muted-foreground hover:border-primary/50"
                  )}
                >
                  {t.onboarding.inputLangFindMore}
                </button>
              </div>

              {/* Selected custom languages (not in common list) */}
              {profile.knownLanguages.filter(code => !commonLanguageCodes.includes(code)).length > 0 && (
                <div className="flex flex-wrap gap-2 justify-center">
                  {profile.knownLanguages
                    .filter(code => !commonLanguageCodes.includes(code))
                    .map((langCode) => {
                      const langData = ALL_LANGUAGES.find(l => l.code === langCode);
                      return (
                        <button
                          key={langCode}
                          type="button"
                          onClick={() => removeLanguage(langCode)}
                          className="px-3 py-1.5 text-sm rounded-full border transition-colors flex items-center gap-1.5 bg-primary text-primary-foreground border-primary"
                        >
                          <span className="w-3.5 h-3.5 rounded border flex items-center justify-center text-[10px] bg-primary-foreground/20 border-primary-foreground/50">
                            ✓
                          </span>
                          {langData && <span>{langData.flag}</span>}
                          {langData?.name || langCode}
                        </button>
                      );
                    })}
                </div>
              )}

              {/* Search input with dropdown */}
              {showCustomLanguage && (
                <div className="relative">
                  <input
                    type="text"
                    placeholder={t.onboarding.inputLangSearch}
                    value={customLanguageInput}
                    onChange={(e) => setCustomLanguageInput(e.target.value)}
                    className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                    autoFocus
                  />
                  {/* Search results dropdown */}
                  {searchResults.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-lg shadow-lg overflow-hidden z-10">
                      {searchResults.map((lang) => (
                        <button
                          key={lang.code}
                          type="button"
                          onClick={() => {
                            addLanguage(lang.code);
                            setCustomLanguageInput("");
                          }}
                          className="w-full px-4 py-2.5 text-left text-sm hover:bg-muted/50 flex items-center gap-2 transition-colors"
                        >
                          <span className="text-base">{lang.flag}</span>
                          <span className="text-foreground">{lang.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Show Continue button when something is selected */}
              {profile.knownLanguages.length > 0 && (
                <div className="flex flex-col items-center gap-3 pt-2">
                  <Button onClick={goToNextStep} className="w-full max-w-xs">
                    {t.onboarding.continue}
                  </Button>
                  <button
                    type="button"
                    onClick={goToNextStep}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {t.onboarding.skipStep}
                  </button>
                </div>
              )}
            </div>
          </InputStep>
        );
      }

      case "input-style":
        return (
          <InputStep
            title={t.onboarding.inputStyleTitle}
            subtitle={t.onboarding.inputStyleSubtitle}
            onNext={goToNextStep}
            onSkip={goToNextStep}
            hideDefaultButtons={isOtherExpanded}
            continueLabel={t.onboarding.continue}
            skipLabel={t.onboarding.skipStep}
          >
            <div className="space-y-3 w-full">
              {/* Beginner option */}
              <button
                type="button"
                onClick={() => {
                  setProfile({ ...profile, speakingStyle: "beginner" });
                  setIsOtherExpanded(false);
                  goToNextStep();
                }}
                className={cn(
                  "w-full p-4 text-left rounded-xl border-2 transition-all",
                  profile.speakingStyle === "beginner" && !isOtherExpanded
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-primary/50 hover:bg-muted/50"
                )}
              >
                <div className="font-semibold text-foreground">{t.onboarding.speakingStyleBeginner}</div>
                <div className="text-sm text-muted-foreground">{t.onboarding.speakingStyleBeginnerDesc}</div>
              </button>

              {/* Like a local option */}
              <button
                type="button"
                onClick={() => {
                  setProfile({ ...profile, speakingStyle: "local" });
                  setIsOtherExpanded(false);
                  goToNextStep();
                }}
                className={cn(
                  "w-full p-4 text-left rounded-xl border-2 transition-all",
                  profile.speakingStyle === "local" && !isOtherExpanded
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-primary/50 hover:bg-muted/50"
                )}
              >
                <div className="font-semibold text-foreground">{t.onboarding.speakingStyleLocal}</div>
                <div className="text-sm text-muted-foreground">{t.onboarding.speakingStyleLocalDesc}</div>
              </button>

            </div>
          </InputStep>
        );

      case "input-gender":
        return (
          <InputStep
            title={t.onboarding.inputGenderTitle}
            subtitle={t.onboarding.inputGenderSubtitle}
            onNext={goToNextStep}
            onSkip={goToNextStep}
            continueLabel={t.onboarding.continue}
            skipLabel={t.onboarding.skipStep}
          >
            <div className="space-y-3 w-full">
              {[
                { value: "male" as const, label: t.onboarding.genderMale },
                { value: "female" as const, label: t.onboarding.genderFemale },
                { value: "other" as const, label: t.onboarding.genderOther },
                { value: "prefer-not-to-say" as const, label: t.onboarding.genderPreferNot },
              ].map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    setProfile({ ...profile, gender: option.value });
                    goToNextStep();
                  }}
                  className={cn(
                    "w-full p-4 text-left rounded-xl border-2 transition-all",
                    profile.gender === option.value
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-primary/50 hover:bg-muted/50"
                  )}
                >
                  <div className="font-medium text-foreground">{option.label}</div>
                </button>
              ))}
            </div>
          </InputStep>
        );

      case "ready":
        return (
          <div className="flex flex-col items-center justify-center min-h-[60vh] px-6 text-center">
            <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
              <Sparkles className="w-10 h-10 text-primary" />
            </div>
            <h1 className="text-3xl font-display text-foreground mb-3">
              {profile.name ? t.onboarding.youreAllSetWithName.replace("{name}", profile.name) : t.onboarding.youreAllSet}
            </h1>
            <p className="text-lg text-muted-foreground max-w-sm mb-6">
              {t.onboarding.startFirstConversation}
            </p>

            {/* Analytics opt-in */}
            <button
              type="button"
              onClick={() => setAnalyticsEnabled(!analyticsEnabled)}
              className="flex items-start gap-3 p-4 rounded-xl bg-muted/50 text-left max-w-sm mb-8"
            >
              <div
                className={cn(
                  "w-5 h-5 mt-0.5 rounded border-2 flex items-center justify-center shrink-0 transition-colors",
                  analyticsEnabled
                    ? "bg-primary border-primary"
                    : "border-muted-foreground/50"
                )}
              >
                {analyticsEnabled && (
                  <svg
                    className="w-3 h-3 text-primary-foreground"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={3}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
              <span className="text-sm text-muted-foreground">
                {t.onboarding.analyticsOptIn}
              </span>
            </button>

            <Button
              onClick={goToNextStep}
              size="lg"
              className="gap-2"
            >
              {t.onboarding.startLearning}
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        );

      case "first-conversation":
        return (
          <div className="flex flex-col items-center justify-center min-h-[60vh] px-6 text-center">
            <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
              <MessageSquare className="w-10 h-10 text-primary" />
            </div>
            <h1 className="text-3xl font-display text-foreground mb-3">
              {t.onboarding.firstConversationTitle}
            </h1>
            <p className="text-lg text-muted-foreground max-w-sm mb-8">
              {t.onboarding.firstConversationSubtitle}
            </p>
            <Button
              onClick={handleComplete}
              size="lg"
              className="gap-2"
            >
              {t.onboarding.firstConversationButton}
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        );
    }
  };

  // Calculate progress for dots (only show for non-welcome and non-ready steps)
  const showNavigation = currentStep !== "welcome" && currentStep !== "ready" && currentStep !== "first-conversation";
  const progressSteps = ALL_STEPS.filter(s => s !== "welcome" && s !== "ready" && s !== "first-conversation");
  const progressIndex = showNavigation ? progressSteps.indexOf(currentStep as typeof progressSteps[number]) : -1;

  return (
    <div className="flex flex-col h-[100dvh] bg-background">
      {/* Navigation bar */}
      {showNavigation && (
        <div className="shrink-0 px-6 pt-6 pb-2">
          <div className="w-full max-w-lg mx-auto flex items-center">
            <button
              type="button"
              onClick={goToPrevStep}
              className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">{t.onboarding.back}</span>
            </button>
            <div className="flex-1 flex justify-center items-center gap-2">
              {progressSteps.map((step, i) => (
                <div
                  key={step}
                  className={cn(
                    "rounded-full transition-all duration-300 ease-out",
                    i === progressIndex
                      ? "w-2.5 h-2.5 bg-primary"
                      : i < progressIndex
                        ? "w-2 h-2 bg-primary/60"
                        : "w-2 h-2 bg-muted-foreground/30"
                  )}
                />
              ))}
            </div>
            {isInputStep ? (
              <button
                type="button"
                onClick={skipToEnd}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {t.onboarding.skipAll}
              </button>
            ) : (
              <div className="w-12" />
            )}
          </div>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto min-h-0 pt-[3vh]">
        {renderStepContent()}
      </div>
    </div>
  );
}

// Feature slide component
function FeatureSlide({
  icon,
  painPoint,
  solution,
  description,
  preview,
  onNext,
  continueLabel = "Continue",
}: {
  icon: React.ReactNode;
  painPoint: string;
  solution: string;
  description: string;
  preview?: React.ReactNode;
  onNext: () => void;
  continueLabel?: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[50vh] px-6 text-center">
      <div className="w-full max-w-lg space-y-6">
        <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto">
          {icon}
        </div>

        <div className="space-y-2">
          <p className="text-muted-foreground line-through decoration-muted-foreground/50">
            {painPoint}
          </p>
          <h2 className="text-2xl font-display text-foreground">
            {solution}
          </h2>
        </div>

        <p className="text-muted-foreground max-w-sm mx-auto">
          {description}
        </p>

        {/* UI Preview - tilted to show it's a mockup */}
        {preview && (
          <div className="pointer-events-none select-none transform -rotate-3">
            {preview}
          </div>
        )}

        <Button
          onClick={onNext}
          size="lg"
          className="gap-2"
        >
          {continueLabel}
          <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

// Static UI preview: Topic creator mockup
function TopicCreatorPreview({
  title,
  subtitle,
  exampleLabel,
  exampleText,
  typeOwnPlaceholder,
}: {
  title: string;
  subtitle: string;
  exampleLabel: string;
  exampleText: string;
  typeOwnPlaceholder: string;
}) {
  return (
    <div className="bg-card border border-border rounded-2xl p-4 shadow-lg max-w-sm mx-auto space-y-4">
      {/* Header */}
      <div className="text-center">
        <h3 className="text-base font-semibold text-foreground">{title}</h3>
        <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
      </div>

      {/* Example card */}
      <div className="relative">
        <div className="p-3 bg-muted/30 border border-dashed border-muted-foreground/30 rounded-xl text-left">
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-[10px] text-muted-foreground">{exampleLabel}</p>
            <p className="text-[10px] text-muted-foreground">1/3</p>
          </div>
          <p className="text-foreground italic text-xs leading-relaxed">&ldquo;{exampleText}&rdquo;</p>
        </div>

        {/* Dots */}
        <div className="flex justify-center gap-1 mt-2">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className={cn(
                "w-1 h-1 rounded-full",
                i === 0 ? "bg-primary" : "bg-muted-foreground/30"
              )}
            />
          ))}
        </div>
      </div>

      {/* Input row */}
      <div className="flex items-center gap-2">
        <div className="flex-1 px-3 py-2 bg-muted/50 rounded-xl text-xs text-muted-foreground text-left">
          {typeOwnPlaceholder}
        </div>
        <div className="w-9 h-9 rounded-xl bg-muted flex items-center justify-center shrink-0">
          <Mic className="w-4 h-4 text-muted-foreground" />
        </div>
      </div>
    </div>
  );
}

// Static UI preview: Voice input mockup
function VoiceInputPreview({ voiceText }: { voiceText: string }) {
  return (
    <div className="bg-card border border-border rounded-2xl p-4 shadow-sm max-w-xs mx-auto space-y-3">
      <div className="flex items-center gap-2">
        <div className="flex-1 px-3 py-2 bg-muted/50 rounded-xl text-sm text-muted-foreground text-left">
          {voiceText}
        </div>
        <div className="w-10 h-10 rounded-xl bg-destructive/90 flex items-center justify-center shrink-0 animate-pulse">
          <Mic className="w-5 h-5 text-white" />
        </div>
        <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center shrink-0">
          <Send className="w-5 h-5 text-primary-foreground" />
        </div>
      </div>
      <div className="flex items-center justify-center gap-1 h-6">
        {[0.3, 0.6, 0.9, 0.5, 0.8, 0.4, 0.7, 0.5].map((h, i) => (
          <div
            key={i}
            className="w-1 bg-destructive/60 rounded-full"
            style={{ height: `${h * 24}px` }}
          />
        ))}
      </div>
    </div>
  );
}

// Interactive UI preview: Dialogue mockup with TTS
function DialoguePreview({ translation }: { translation: string }) {
  const tts = useTTS();
  const phrase = "Bom dia! O que deseja?";

  const handleSpeakerClick = () => {
    if (tts.state === "playing") {
      tts.stop();
    } else {
      tts.speak(phrase);
    }
  };

  return (
    <div className="space-y-3 max-w-xs mx-auto pointer-events-auto">
      {/* Their message */}
      <div className="flex gap-2">
        <div className="bg-muted rounded-2xl rounded-tl-md px-4 py-2 text-sm text-left max-w-[80%]">
          <p className="text-foreground">{phrase}</p>
          <p className="text-xs text-muted-foreground mt-1 italic">{translation}</p>
        </div>
        <SpeakerButton
          state={tts.state}
          onClick={handleSpeakerClick}
          className="bg-primary/10 shrink-0 self-end"
          size="md"
        />
      </div>
      {/* Your message */}
      <div className="flex gap-2 justify-end">
        <div className="bg-primary rounded-2xl rounded-tr-md px-4 py-2 text-sm text-left max-w-[80%]">
          <p className="text-primary-foreground">Um café, por favor.</p>
        </div>
      </div>
    </div>
  );
}

// Input step wrapper component
function InputStep({
  title,
  subtitle,
  children,
  onNext,
  onSkip,
  hideDefaultButtons = false,
  continueLabel = "Continue",
  skipLabel = "Skip this step",
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
  onNext: () => void;
  onSkip: () => void;
  hideDefaultButtons?: boolean;
  continueLabel?: string;
  skipLabel?: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[50vh] px-6">
      <div className="w-full max-w-lg space-y-6">
        <div className="text-center space-y-2">
          <h2 className="text-2xl font-display text-foreground">
            {title}
          </h2>
          <p className="text-muted-foreground">
            {subtitle}
          </p>
        </div>

        {children}

        {!hideDefaultButtons && (
          <div className="flex flex-col items-center gap-3 pt-4">
            <Button onClick={onNext} className="w-full max-w-xs">
              {continueLabel}
            </Button>
            <button
              type="button"
              onClick={onSkip}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              {skipLabel}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
